import { useEffect, useRef, useState } from "react";
import {
  MdAccessTime,
  MdCalendarToday,
  MdDelete,
  MdEdit,
  MdForum,
  MdHourglassBottom,
  MdMarkEmailRead,
  MdWarningAmber,
} from "react-icons/md";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { FieldGroup, FormModal, Input, SelectField, Textarea } from "../../components/ui";
import { HabBadge, habColor } from "../../shared/habilitations";
import HighlightText from "../../shared/HighlightText";
import { formatDateFr } from "../../shared/dateFormat";
import { statutDroitColor } from "../../shared/statusColors";
import { COLORS, RADIUS } from "../../shared/theme";

const TYPE_DROIT = ["Accès (art. 15)", "Rectification (art. 16)", "Effacement (art. 17)", "Portabilité (art. 20)", "Opposition (art. 21)", "Limitation (art. 18)", "Retrait consentement", "Autre"];
const STATUT_DROIT = ["Reçue", "En cours", "Complétée", "Refusée", "Hors délai"];
const CANAL_DROIT = ["Email", "Formulaire web", "Courrier postal", "Téléphone", "En personne"];

/** Date au format input `YYYY-MM-DD` → même format après +`days` jours calendaires (timezone locale). */
function addCalendarDaysIso(isoYmd, days) {
  if (!isoYmd || typeof isoYmd !== "string") return "";
  const m = isoYmd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

const emptyDroit = { type: "Accès (art. 15)", statut: "Reçue", canal: "Email", dateReception: "", dateEcheance: "", dateReponse: "", responsableId: "", identiteDecouverte: false, description: "", reponse: "", observations: "" };

export default function PageDroits({ droits, setDroits, personnes, searchHighlight }) {
  const [showForm, setShowForm] = useState(false);
  const highlightRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("Tous");
  const nextId = useRef(1);
  const [form, setForm] = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCheck = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));
  const setDateReception = (e) => {
    const v = e.target.value;
    setForm((f) => ({
      ...f,
      dateReception: v,
      dateEcheance: v ? addCalendarDaysIso(v, 30) : "",
    }));
  };

  const openNew = () => { setEditing(null); setForm({ ...emptyDroit }); setShowForm(true); };
  const openEdit = (d) => {
    setEditing(d);
    const copy = { ...d };
    if (copy.dateReception && !copy.dateEcheance) {
      copy.dateEcheance = addCalendarDaysIso(copy.dateReception, 30);
    }
    setForm(copy);
    setShowForm(true);
  };
  const save = () => {
    if (editing) setDroits(ds => ds.map(d => d.id === editing.id ? { ...form, id: editing.id } : d));
    else setDroits(ds => [...ds, { ...form, id: nextId.current++ }]);
    setShowForm(false); setEditing(null); setForm(null);
  };

  useEffect(() => {
    const nums = droits.map((d) => Number(d?.id)).filter((n) => Number.isFinite(n) && n >= 0);
    if (nums.length > 0) {
      nextId.current = Math.max(nextId.current, Math.max(...nums) + 1);
    }
  }, [droits]);

  const pOptions = [{ value: "", label: "— Sélectionner —" }, ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom} (${p.habilitation})` }))];
  const now = new Date();
  const filtered = filter === "Tous" ? droits : droits.filter(d => d.statut === filter);
  const rawHighlightId = searchHighlight?.id != null ? Number(searchHighlight.id) : null;
  const highlightItemType = searchHighlight?.itemType || null;
  const highlightId =
    rawHighlightId != null && (!highlightItemType || highlightItemType === "droit") ? rawHighlightId : null;
  const searchTerm = searchHighlight?.searchTerm || null;

  useEffect(() => {
    if (highlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId]);

  const stats = {
    total: droits.length,
    enCours: droits.filter(d => ["Reçue","En cours"].includes(d.statut)).length,
    horsDelai: droits.filter(d => d.dateEcheance && new Date(d.dateEcheance) < now && !["Complétée","Refusée"].includes(d.statut)).length,
    completees: droits.filter(d => d.statut === "Complétée").length,
  };

  return (
    <PageShell>
      <PageHeader
        title="Droits des personnes"
        subtitle="Délai de réponse légal : 30 jours (art. 12 RGPD)"
        actions={
          <button type="button" className="page-shell__btn page-shell__btn--primary" onClick={openNew}>
            + Nouvelle demande
          </button>
        }
      />

      <PageContent>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 22 }}>
          {[
            { label: "Total demandes", val: stats.total, color: "#064e3b", bg: "#ecfdf5", border: "#6ee7b7" },
            { label: "En cours", val: stats.enCours, color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
            { label: "Hors délai", val: stats.horsDelai, color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" },
            { label: "Complétées", val: stats.completees, color: "#065f46", bg: "#d1fae5", border: "#86efac" },
          ].map(k => (
            <div key={k.label} style={{ background: k.bg, borderRadius: RADIUS.lg, padding: "14px 18px", border: `1.5px solid ${k.border}` }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.val}</div>
              <div style={{ fontSize: 12, color: k.color, marginTop: 3, fontWeight: 600 }}>{k.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 16, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 600 }}>Statut :</span>
          {["Tous", ...STATUT_DROIT].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{ padding: "4px 12px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: filter === s ? "#064e3b" : "#fff", color: filter === s ? "#fff" : "#374151", border: `1.5px solid ${filter === s ? "#064e3b" : COLORS.border}` }}>{s}</button>
          ))}
        </div>

        {showForm && form && (
          <FormModal
            title={editing ? "Modifier la demande" : "Nouvelle demande d'exercice de droits"}
            headerBackground="#064e3b"
            maxWidth={720}
            footer={
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" onClick={() => { setShowForm(false); setForm(null); setEditing(null); }} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Annuler</button>
                <button type="button" onClick={save} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
              </div>
            }
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FieldGroup label="Type de droit"><SelectField value={form.type} onChange={set("type")} options={TYPE_DROIT} /></FieldGroup>
              <FieldGroup label="Canal de réception"><SelectField value={form.canal} onChange={set("canal")} options={CANAL_DROIT} /></FieldGroup>
              <FieldGroup label="Statut"><SelectField value={form.statut} onChange={set("statut")} options={STATUT_DROIT} /></FieldGroup>
              <FieldGroup label="Responsable traitement"><SelectField value={form.responsableId} onChange={set("responsableId")} options={pOptions} /></FieldGroup>
              <FieldGroup label="Date de réception"><Input type="date" value={form.dateReception} onChange={setDateReception} /></FieldGroup>
              <FieldGroup label="Échéance légale (30j)"><Input type="date" value={form.dateEcheance} onChange={set("dateEcheance")} /></FieldGroup>
              <FieldGroup label="Date de réponse"><Input type="date" value={form.dateReponse} onChange={set("dateReponse")} /></FieldGroup>
              <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 22 }}>
                <input type="checkbox" id="identite" checked={form.identiteDecouverte} onChange={setCheck("identiteDecouverte")} />
                <label htmlFor="identite" style={{ fontSize: 13, cursor: "pointer" }}>Identité de la personne vérifiée</label>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Description de la demande"><Textarea value={form.description} onChange={set("description")} rows={2} placeholder="Résumé de la demande reçue…" /></FieldGroup>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Réponse apportée"><Textarea value={form.reponse} onChange={set("reponse")} rows={2} placeholder="Actions réalisées, données transmises, motif de refus…" /></FieldGroup>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Observations"><Textarea value={form.observations} onChange={set("observations")} rows={1} placeholder="Notes de suivi…" /></FieldGroup>
              </div>
            </div>
          </FormModal>
        )}

        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: COLORS.textMuted, fontSize: 14 }}>Aucune demande enregistrée.</div>
        ) : filtered.map(d => {
          const sc = statutDroitColor[d.statut] || statutDroitColor["Reçue"];
          const resp = personnes.find(p => p.id === d.responsableId || p.id === Number(d.responsableId));
          const hc = resp ? (habColor[resp.habilitation] || habColor["Autre"]) : null;
          const isLate = d.dateEcheance && new Date(d.dateEcheance) < now && !["Complétée","Refusée"].includes(d.statut);
          const daysLeft = d.dateEcheance ? Math.ceil((new Date(d.dateEcheance) - now) / 86400000) : null;
          const isHighlighted = d.id === highlightId || Number(d.id) === highlightId;
          return (
            <div
              key={d.id}
              ref={isHighlighted ? highlightRef : null}
              style={{
                background: isHighlighted ? "#fffbeb" : COLORS.white,
                borderRadius: RADIUS.lg,
                border: `1.5px solid ${isHighlighted ? "#f59e0b" : isLate ? "#dc2626" : COLORS.border}`,
                padding: 18,
                marginBottom: 12,
                boxShadow: isHighlighted ? "0 0 0 2px #fef3c7" : "0 1px 4px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.2s",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: COLORS.textPrimary, marginBottom: 5 }}><HighlightText text={d.type} term={searchTerm} /></div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#f1f5f9", color: "#374151", border: `1px solid ${COLORS.border}`, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><MdMarkEmailRead size={12} /> {d.canal}</span>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, fontWeight: 700 }}>{d.statut}</span>
                    {d.identiteDecouverte && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#d1fae5", color: "#065f46", border: "1px solid #6ee7b7", fontWeight: 700 }}>✓ Identité vérifiée</span>}
                    {isLate && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 4 }}><MdWarningAmber size={13} /> HORS DÉLAI</span>}
                    {!isLate && daysLeft !== null && daysLeft <= 7 && daysLeft >= 0 && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}><MdHourglassBottom size={13} /> {daysLeft}j restants</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => openEdit(d)} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdEdit size={14} /></button>
                  <button onClick={() => setDroits(ds => ds.filter(x => x.id !== d.id))} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdDelete size={14} /></button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8 }}><HighlightText text={d.description} term={searchTerm} /></div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11, alignItems: "center" }}>
                {resp && hc && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 22, height: 22, borderRadius: "50%", background: hc.bg, border: `1.5px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: hc.text }}>{resp.prenom[0]}{resp.nom[0]}</div><span>{resp.prenom} {resp.nom}</span><HabBadge habilitation={resp.habilitation} /></div>}
                {d.dateReception && <span style={{ color: COLORS.textSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}><MdCalendarToday size={13} /> Reçue le {formatDateFr(d.dateReception)}</span>}
                {d.dateEcheance && <span style={{ color: isLate ? "#dc2626" : "#6b7280", fontWeight: isLate ? 700 : 400, display: "inline-flex", alignItems: "center", gap: 4 }}><MdAccessTime size={13} /> Échéance {formatDateFr(d.dateEcheance)}</span>}
                {d.dateReponse && <span style={{ color: "#065f46" }}>✓ Répondue le {formatDateFr(d.dateReponse)}</span>}
              </div>
              {d.reponse && <div style={{ marginTop: 8, padding: "6px 10px", background: "#f0fdf4", borderRadius: RADIUS.sm, fontSize: 11, color: "#15803d", display: "inline-flex", alignItems: "center", gap: 4 }}><MdForum size={13} /> {d.reponse}</div>}
            </div>
          );
        })}
      </PageContent>
    </PageShell>
  );
}
